import { Container, Filter, Matrix, Point, Rectangle, Sprite, Texture } from "pixi.js"
import { spriteContentWorldBounds } from "./texture_content_bounds"
import type { HighlightFrame } from "./entity_highlight"

// Subtract the original alpha from its dilation: the centre stays transparent even
// when the outline is drawn above a building rather than underneath the body.
const fragment = `precision mediump float;
varying vec2 vTextureCoord;
uniform sampler2D uSampler;
uniform vec4 inputSize;
uniform vec4 inputClamp;
void main(void) {
    float centre = texture2D(uSampler, vTextureCoord).a;
    float edge = 0.0;
    for (int i = 0; i < 8; i++) {
        float angle = float(i) * 0.78539816339;
        vec2 offset = vec2(cos(angle), sin(angle)) * inputSize.zw * 2.0;
        edge = max(edge, texture2D(uSampler, clamp(vTextureCoord + offset, inputClamp.xy, inputClamp.zw)).a);
    }
    float alpha = max(0.0, edge - centre);
    gl_FragColor = vec4(vec3(0.45, 0.90, 1.0) * alpha, alpha);
}`

/** The storeys drawn over the player, asked in screen space at the player's depth. */
export interface SceneOcclusion {
    /** Whether any of them draws over part of `bounds`. */
    overlaps(bounds: Rectangle, z: number): boolean
    /** Whether, between them, they leave none of `bounds` showing. */
    covers(bounds: Rectangle, z: number): boolean
}

/**
 * How long, in ms, the answer to "is the body hidden?" holds before it is asked again. The outline keeps
 * following the pose every frame; only the question, which measures the storeys' art, waits.
 */
export const OCCLUSION_RECHECK_MS = 300

/** Live: window.occlusionOutline (`= false` turns the outline and its checks off; `.recheckMs` sets the rate). */
export const occlusionOutlineConfig = (): { recheckMs: number } | undefined => {
    const defaults = { recheckMs: OCCLUSION_RECHECK_MS }
    if (typeof window === "undefined") return defaults
    const w = window as any
    if (w.occlusionOutline === undefined) w.occlusionOutline = defaults
    return w.occlusionOutline && typeof w.occlusionOutline === "object" ? { ...defaults, ...w.occlusionOutline } : undefined
}

/** One borrowed animation frame, updated after the local player's pose and position. */
export class PlayerOcclusionOutline {
    private hidden = false
    private checkedAt = -Infinity
    private root = new Container()
    private pose = new Sprite(Texture.EMPTY)
    private contentBounds = new Rectangle()
    private relative = new Matrix()
    private origin = new Point()
    private transformedOrigin = new Point()
    // Pixi defaults to highp in the vertex stage and mediump in the fragment
    // stage on desktop. Shared uniforms must match for WebGL to link the shader.
    readonly filter = new Filter(`precision mediump float;
${Filter.defaultVertexSrc}`, fragment)

    constructor(private surface: Container, private recheckMs?: number) {
        this.filter.padding = 3
        this.pose.filters = [this.filter]
        this.root.addChild(this.pose)
        this.root.zIndex = 999999
        this.root.interactive = this.root.interactiveChildren = false
        this.root.visible = false
        surface.addChild(this.root)
    }

    update(body: { container: Container; frame: HighlightFrame } | undefined, playerZ: number,
        scene: SceneOcclusion): void {
        this.root.visible = false
        if (!body || !body.container.worldVisible) { this.hide(); return }
        const { container, frame } = body
        container.toGlobal(this.origin, this.transformedOrigin)
        if (container.worldAlpha <= 0.15) { this.hide(); return }
        this.pose.texture = frame.texture
        this.pose.position.set(frame.x, frame.y)
        this.pose.scale.set(frame.scaleX, frame.scaleY)
        this.pose.anchor.set(frame.anchorX ?? 0, frame.anchorY ?? 0)
        // Refresh the source transform, including stair travel, falls and camera zoom.
        this.surface.toGlobal(this.origin, this.transformedOrigin)
        const relative = this.relative.copyFrom(this.surface.worldTransform).invert().append(container.worldTransform)
        this.root.transform.setFromMatrix(relative)
        const now = Date.now()
        if (now - this.checkedAt >= (this.recheckMs ?? occlusionOutlineConfig()?.recheckMs ?? OCCLUSION_RECHECK_MS)) {
            this.checkedAt = now
            const bounds = this.pose.getBounds()
            // Only read visible pixels after a cheap overlap succeeds. The existing
            // texture cache reads each shared pose once, then reuses its tight bounds;
            // a pose not read yet counts as clear until it is read, when the browser is idle.
            const content = scene.overlaps(bounds, playerZ) ? spriteContentWorldBounds(this.pose, this.contentBounds) : undefined
            // Outlined only once nothing of the body shows: half under a storey, the player can still see itself.
            this.hidden = content !== undefined && scene.covers(content ?? bounds, playerZ)
        }
        if (this.hidden) {
            this.root.alpha = container.worldAlpha / this.surface.worldAlpha
            this.root.visible = true
        } else {
            this.pose.texture = Texture.EMPTY
        }
    }

    hide(): void {
        this.root.visible = false
        this.pose.texture = Texture.EMPTY
        // Ask afresh as soon as the outline may show again (off a ladder, back under a storey).
        this.hidden = false
        this.checkedAt = -Infinity
    }

    destroy(): void {
        this.root.destroy({ children: true })
        this.filter.destroy()
    }
}
